'use client'
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface LightboxProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLoading?: boolean;
}

const Lightbox: React.FC<LightboxProps> = ({
  open,
  onClose,
  children,
  isLoading = false
}) => {
  const theme = useTheme();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (open && !isLoading) {
      const timer = setTimeout(() => setShowContent(true), 100);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [open, isLoading]);

  const handleClose = useCallback(() => {
    setShowContent(false);
    setTimeout(() => onClose(), 200);
  }, [onClose]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            maxHeight: 'none',
            width: '100vw',
            height: '100dvh',
            margin: 0,
            borderRadius: 0,
          }
        }
      }}
    >
      <DialogContent
        sx={{
          backgroundColor: theme.palette.grey.A100,
          width: '100vw',
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, md: 5 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: { xs: 24, md: 40 },
            right: { xs: 24, md: 40 },
            width: { xs: 32, md: 56 },
            height: { xs: 32, md: 56 },
            backgroundColor: 'common.white',
            color: 'primary.600',
            zIndex: 1000,
            '&:hover': {
              backgroundColor: 'grey.100',
            },
            '& svg': {
              width: 24,
              height: 24,
            },
          }}
          aria-label="Close lightbox"
        >
          <Close />
        </IconButton>

        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CircularProgress 
              size={60} 
              thickness={4} 
              sx={{
                color: 'primary.main',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }} 
            />
          </Box>
        )}
        
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '70dvh', md: 'calc(100dvh - 160px)' },
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 40, md: 40 },
          paddingX: { xs: 0, md: 92 },
          transition: 'opacity 0.4s ease-in-out, transform 0.4s ease-in-out',
          overflow: 'hidden',
          boxSizing: 'border-box',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'scale(1)' : 'scale(0.95)',
          '& > div': {
            width: '100% !important',
            height: '100% !important',
            position: 'relative !important',
          },
          '@supports (-webkit-touch-callout: none)': {
            height: '70dvh',
            minHeight: '50dvh',
            maxHeight: '80dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}>
          {children}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Lightbox;